// Arredonda com 1 casa
const round1 = (n) => Number((Number(n) || 0).toFixed(1));

/** Converte instância (centro) -> top-left (pra pontos de luz) */
export function centerToTopLeft(inst, { clamp = false } = {}) {
  const w = round1(inst.width ?? 2.5);
  const h = round1(inst.height ?? 5);

  let x = round1((inst.x ?? 0) - w / 2);
  let y = round1((inst.y ?? 0) - h / 2);

  if (clamp) {
    x = round1(Math.max(0, Math.min(100 - w, x)));
    y = round1(Math.max(0, Math.min(100 - h, y)));
  }

  return { x, y, width: w, height: h };
}

/** Converte instância (top-left) -> centro (pra edição dos pontos) */
export function topLeftToCenter(inst) {
  const w = round1(inst.width ?? 2.5);
  const h = round1(inst.height ?? 5);

  const x = round1((inst.x ?? 0) + w / 2);
  const y = round1((inst.y ?? 0) + h / 2);

  return { x, y, width: w, height: h };
}

/**
 * Converte o estado do editor (centro) -> formato FINAL (top-left)
 * - só existe esse formato
 * - garante:
 *   - instances com todos os "light" antes dos "led"
 */
export function editorToSap(lightPoints, { clamp = false } = {}) {
  return (lightPoints || []).map((group) => {
    const hasInstances =
      Array.isArray(group.instances) && group.instances.length > 0;

    const baseGroup = {
      id: group.id,
      modulo: group.modulo,
      idx: group.idx,
      descricao: group.descricao,
      circleNumber: group.circleNumber
    };

    if (hasInstances) {
      let instances = group.instances.map((inst) => {
        // LED: mantém x/y/width/height como estão
        if (inst.tipo === 'led') {
          return {
            tipo: 'led',
            x: inst.x ?? 0,
            y: inst.y ?? 0,
            width: inst.width ?? 100,
            height: inst.height ?? 100,
            ledPath: inst.ledPath,
            ledColor: inst.ledColor ?? '#f4bb00',
            ledMode: inst.ledMode ?? 'static',
            ledStrokeWidth: inst.ledStrokeWidth ?? 0.25
          };
        }

        // PONTO DE LUZ
        const { x, y, width, height } = centerToTopLeft(inst, { clamp });
        return {
          tipo: 'light',
          x,
          y,
          width,
          height,
          circleNumber: inst.circleNumber ?? group.circleNumber
        };
      });

      // 🔥 Ordena: LIGHT primeiro, depois LED
      const pesoTipo = (t) => (t === 'led' ? 1 : 0);
      instances = instances.sort(
        (a, b) => pesoTipo(a.tipo) - pesoTipo(b.tipo)
      );

      baseGroup.instances = instances;
    }

    Object.keys(baseGroup).forEach((k) => {
      if (typeof baseGroup[k] === 'undefined') delete baseGroup[k];
    });

    return baseGroup;
  });
}

/**
 * Converte o formato FINAL (top-left) -> estado do editor (centro)
 */
export function sapToEditor(sapConfig = []) {
  return sapConfig.map((group) => {
    const rawInstances = Array.isArray(group.instances)
      ? group.instances
      : [];

    const baseGroup = {
      id: group.id,
      modulo: String(group.modulo),
      idx: String(group.idx),
      descricao: group.descricao,
      circleNumber: group.circleNumber
    };

    if (rawInstances.length) {
      baseGroup.instances = rawInstances.map((inst) => {
        // LED volta praticamente igual
        if (inst.tipo === 'led') {
          return {
            tipo: 'led',
            x: inst.x ?? 0,
            y: inst.y ?? 0,
            width: inst.width ?? 100,
            height: inst.height ?? 100,
            ledPath: inst.ledPath ?? '',
            ledColor: inst.ledColor ?? '#f4bb00',
            ledMode: inst.ledMode ?? 'static',
            ledStrokeWidth: inst.ledStrokeWidth ?? 0.25
          };
        }

        // LIGHT volta pra centro
        const { x, y, width, height } = topLeftToCenter(inst);
        return {
          tipo: 'light',
          x,
          y,
          width,
          height,
          circleNumber: inst.circleNumber ?? group.circleNumber
        };
      });
    }

    Object.keys(baseGroup).forEach((k) => {
      if (typeof baseGroup[k] === 'undefined') delete baseGroup[k];
    });

    return baseGroup;
  });
}

/**
 * Extrai o array do arquivo .js
 *  - recebe o texto de um arquivo do tipo:
 *    export const iluminacaoConfig = [ { ... } ];
 *  - retorna o ARRAY pra você fazer sapToEditor nele
 */
export function extractConfigFromJsFile(
  fileText,
  exportName = 'iluminacaoConfig'
) {
  const regex = new RegExp(`export\\s+const\\s+${exportName}\\s*=\\s*`);
  const matchIndex = fileText.search(regex);

  if (matchIndex === -1) {
    throw new Error(`Não encontrei "export const ${exportName} =" no arquivo.`);
  }

  let content = fileText.slice(matchIndex).replace(regex, '').trim();

  if (content.endsWith(';')) {
    content = content.slice(0, -1).trim();
  }

  try {
    // Aceita JS com chaves sem aspas
    const fn = new Function('"use strict"; return (' + content + ');');
    const result = fn();
    if (!Array.isArray(result)) {
      throw new Error('O conteúdo exportado não é um array.');
    }
    return result;
  } catch (err) {
    throw new Error('Erro ao interpretar o arquivo JS: ' + err.message);
  }
}